import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SiteNavbar from "./SiteNavbar";
import SiteFooter from "./SiteFooter";

const ease = [0.21, 0.47, 0.32, 0.98];

const primaryBtnClass =
  "inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]";

const primaryBtnStyle = {
  background: "var(--brand)",
  color: "var(--on-brand)",
  boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
};

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const StaticPageLayout = ({ page, testId }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page.id]);

  return (
    <div
      data-testid={testId}
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease }}
            className="text-center mb-14 md:mb-20"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              {page.eyebrow}
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              {page.title}{" "}
              {page.titleAccent ? (
                <span className="italic" style={{ color: "var(--brand)" }}>
                  {page.titleAccent}
                </span>
              ) : null}
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-soft)" }}
            >
              {page.intro}
            </p>
          </motion.div>

          {page.sections?.map((section, i) => (
            <FadeIn key={section.heading} delay={i * 0.06} className="mb-12">
              <h2
                className="font-serif text-2xl md:text-3xl font-medium mb-4"
                style={{ color: "var(--text)" }}
              >
                {section.heading}
              </h2>
              {section.paragraphs?.map((p) => (
                <p
                  key={p.slice(0, 40)}
                  className="text-base leading-relaxed mb-4 last:mb-0"
                  style={{ color: "var(--text-soft)" }}
                >
                  {p}
                </p>
              ))}
              {section.list?.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {section.list.map((item) => (
                    <li
                      key={item}
                      className="flex gap-3 text-sm leading-relaxed"
                      style={{ color: "var(--text-soft)" }}
                    >
                      <span style={{ color: "var(--brand)" }}>·</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </FadeIn>
          ))}

          {page.cards?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {page.cards.map((card, i) => (
                <FadeIn key={card.title} delay={i * 0.08}>
                  <div
                    className="cozy-card rounded-3xl p-6 h-full"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <h3
                      className="font-serif text-lg font-medium mb-2"
                      style={{ color: "var(--text)" }}
                    >
                      {card.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--text-soft)" }}
                    >
                      {card.desc}
                    </p>
                  </div>
                </FadeIn>
              ))}
            </div>
          )}

          {page.cta && (
            <FadeIn>
              <div
                className="rounded-3xl p-10 md:p-12 text-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
                  border: "1px solid var(--border)",
                }}
              >
                <h2
                  className="font-serif text-2xl md:text-3xl font-medium mb-3"
                  style={{ color: "var(--text)" }}
                >
                  {page.cta.title}
                </h2>
                <p
                  className="text-base mb-6 max-w-md mx-auto"
                  style={{ color: "var(--text-soft)" }}
                >
                  {page.cta.desc}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  {page.cta.primaryHref ? (
                    <a
                      href={page.cta.primaryHref}
                      {...(page.cta.primaryHref.startsWith("http")
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className={primaryBtnClass}
                      style={primaryBtnStyle}
                    >
                      {page.cta.primaryLabel}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link
                      to={page.cta.primaryTo || "/"}
                      className={primaryBtnClass}
                      style={primaryBtnStyle}
                    >
                      {page.cta.primaryLabel}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                  {page.cta.secondaryTo && (
                    <Link
                      to={page.cta.secondaryTo}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                      style={{
                        borderColor: "var(--border)",
                        color: "var(--text)",
                        background: "var(--surface)",
                      }}
                    >
                      {page.cta.secondaryLabel}
                    </Link>
                  )}
                </div>
              </div>
            </FadeIn>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default StaticPageLayout;
